import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import {
  getThirdPartyAuthorisationURLWithQueryParamsAndSetState,
  thirdPartySignInAndUp,
} from 'supertokens-auth-react/recipe/thirdpartypasswordless';
import { Variant } from '@/components/compound-components/settings/integrations-tab/integrations-tab';
import { Button } from '@/components/shadcn/button';
import {
  PostChannel,
  SchedulingVariantV1Channel,
} from '@/__generated__/graphql';
import { useOrganizationContext } from '@/contexts/organization-context';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { Spinner } from '@/components/basic/spinner';
import { env } from 'next-runtime-env';
import { useToast } from '@/components/shadcn/use-toast';
import { useSettingsConntext } from '@/contexts/settings-context';
import { setCookieWithExpiry } from '@/lib/utils';
import {
  IS_HANDLE_CALLBACK_QUERY_PARAM,
  SOCIAL_PLATFORM_QUERY_PARAM,
} from '@/consts/social-media';
import { logError } from '@/lib/rum';

export type ChannelPage = {
  id: string;
  type: Exclude<PostChannel, PostChannel.Unspecified>;
  name: string;
  pageToken?: string;
};

export type SocialMediaPlatform = 'linkedin' | 'meta' | 'google';

type ConnectButtonProps = {
  thirdPartyId: 'custom-linkedin' | 'facebook' | 'facebook-business' | 'google';
  socialMediaPlatform: SocialMediaPlatform;
  variant: Variant;
  setLinkedSocialMedia: (channels: SchedulingVariantV1Channel[]) => void;
  setConnectedGoogleAccount: (gmail: string) => void;
};

const getPlatformChannels = (socialMediaPlatform: SocialMediaPlatform) => {
  switch (socialMediaPlatform) {
    case 'linkedin':
      return [PostChannel.LinkedIn];
    case 'meta':
      return [PostChannel.Facebook, PostChannel.Instagram];
    case 'google':
      return [PostChannel.Unspecified];
    default:
      return [];
  }
};

function IntegrationConnectBtn({
  thirdPartyId,
  socialMediaPlatform,
  setConnectedGoogleAccount,
}: ConnectButtonProps) {
  const { t } = useTranslation();
  const { organizationId } = useOrganizationContext();
  const {
    isIntegrationLoading,
    setIntegrationLoading,
    setIntegrationPageList,
    setIntegrationPagesCheckedToLink,
    setIntegrationShowPageLinking,
    setIntegrationPlatformChannels,
  } = useSettingsConntext();
  const session = useSessionContext();
  const router = useRouter();
  const { isHandleCallback, socialPlatform } = router.query;
  const { toast } = useToast();
  const [] = useState(false);

  const startPagesLinkingFlow = async (
    pagesGrantedAccess: ChannelPage[],
    socialMediaPlatform: SocialMediaPlatform
  ) => {
    const socialMedialChannels = getPlatformChannels(socialMediaPlatform);

    setIntegrationPageList && setIntegrationPageList(pagesGrantedAccess);

    setIntegrationPlatformChannels &&
      setIntegrationPlatformChannels(socialMedialChannels);
    setIntegrationShowPageLinking && setIntegrationShowPageLinking(true);

    const facebookAndInstagramCheck =
      pagesGrantedAccess.length === 2 &&
      pagesGrantedAccess.filter((page) => page.type === 'Facebook').length ===
        1 &&
      pagesGrantedAccess.filter((page) => page.type === 'Instagram').length ===
        1;
    if (pagesGrantedAccess.length === 1 || facebookAndInstagramCheck) {
      setIntegrationPagesCheckedToLink &&
        setIntegrationPagesCheckedToLink(pagesGrantedAccess);
      setIntegrationLoading && setIntegrationLoading(false);
    } else if (!pagesGrantedAccess.length) {
      setIntegrationPagesCheckedToLink && setIntegrationPagesCheckedToLink([]);
      setIntegrationLoading && setIntegrationLoading(false);
      setIntegrationPlatformChannels &&
        setIntegrationPlatformChannels(undefined);
      toast({
        description: 'You have no pages to link',
        duration: 3000,
        variant: 'destructive',
      });
    } else {
      setIntegrationPagesCheckedToLink && setIntegrationPagesCheckedToLink([]);
      setIntegrationLoading && setIntegrationLoading(false);
    }
  };

  const buildSocialMediaIntegrationServiceRequestBody = useCallback(
    (organizationId: string, thirdPartyResponse: any) => {
      switch (socialMediaPlatform) {
        case 'linkedin':
          return {
            organizationId,
            ...thirdPartyResponse.user.thirdParty[0].rawData,
          };
        case 'meta':
          return {
            organizationId,
            accessToken: thirdPartyResponse.user.thirdParty[0].accessToken,
            userId: thirdPartyResponse.user.thirdParty[0].userId,
            isNewFlow: true,
          };
        default:
          return {};
      }
    },
    [socialMediaPlatform]
  );

  const grantAccessToOurSocialMediaIntegrationService = async (
    organizationId: string,
    thirdPartyResponse: any
  ) => {
    const isNewFBLogin = thirdPartyId == 'facebook-business';
    const INTEGRATION_PATH = `/${socialMediaPlatform}${isNewFBLogin ? '/auth/v2' : '/auth'}`;
    const requestBody = buildSocialMediaIntegrationServiceRequestBody(
      organizationId,
      thirdPartyResponse
    );

    const response = await fetch(
      `${env('AUTHENTICATION_DOMAIN')}${INTEGRATION_PATH}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  };

  async function handleConnectClick() {
    if (isIntegrationLoading) return;
    setIntegrationLoading && setIntegrationLoading(true);

    try {
      const url = new URL(window.location.href);
      const redirectUrl = `${url.origin}/social-media-callback?${IS_HANDLE_CALLBACK_QUERY_PARAM}=true&${SOCIAL_PLATFORM_QUERY_PARAM}=${socialMediaPlatform}`;

      const authUrl =
        await getThirdPartyAuthorisationURLWithQueryParamsAndSetState({
          thirdPartyId,
          frontendRedirectURI: redirectUrl,
        });

      organizationId &&
        setCookieWithExpiry('organizationId', organizationId, 240);
      router.push(authUrl);
    } catch (err) {
      logError('Connection error:', err);
      toast({
        description: 'Something went wrong please try again.',
        duration: 3000,
        variant: 'destructive',
      });
    } finally {
      setIntegrationLoading && setIntegrationLoading(false);
    }
  }

  useEffect(() => {
    if (isHandleCallback && socialPlatform === socialMediaPlatform) {
      const updatedQuery = { ...router.query };
      delete updatedQuery[IS_HANDLE_CALLBACK_QUERY_PARAM];

      const queryString = new URLSearchParams(
        updatedQuery as unknown as string
      ).toString();
      router.push('/', { search: queryString });

      handleThirdPartyProviderCallback();
    }

    async function handleThirdPartyProviderCallback() {
      try {
        const response = await thirdPartySignInAndUp();

        if (response.status === 'OK') {
          if (response.user.thirdParty[0].id === 'google') {
            const gmailAccount = response.user?.emails[0] || '';
            setConnectedGoogleAccount(gmailAccount);
            setIntegrationPagesCheckedToLink &&
              setIntegrationPagesCheckedToLink([]);
            setIntegrationLoading && setIntegrationLoading(false);
            return;
          }

          const { pagesGrantedAccess } =
            await grantAccessToOurSocialMediaIntegrationService(
              organizationId as string,
              response
            );

          if (!pagesGrantedAccess || pagesGrantedAccess.length === 0) {
            toast({
              description: `No pages found related to your ${socialMediaPlatform} account`,
              duration: 3000,
              variant: 'destructive',
            });
            setIntegrationLoading && setIntegrationLoading(false);
            return;
          }

          startPagesLinkingFlow(pagesGrantedAccess, socialMediaPlatform);
        }
      } catch (err) {
        toast({
          description: 'Something went wrong please try again.',
          duration: 3000,
          variant: 'destructive',
        });
        setIntegrationLoading && setIntegrationLoading(false);
      }
    }
  }, []);

  if (session.loading || !session.doesSessionExist) {
    return null;
  }

  return (
    <div className='flex gap-2'>
      <Button
        className='m-0 mt-auto h-7 rounded border-0 bg-meta text-white hover:bg-meta-foreground hover:text-white hover:shadow-lg'
        disabled={isIntegrationLoading}
        onClick={handleConnectClick}
      >
        {isIntegrationLoading && <Spinner className='mr-2 w-4' />}
        {t('account-menu-settings.connect')}
      </Button>
    </div>
  );
}

export default IntegrationConnectBtn;
